import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";

import ModalPopUp from "@/Components/common/ModalPopUp";
import TableLoader from "@/Components/common/TableLoader";
import TableNoContent from "@/Components/common/TableNoContent";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import DataTablePaginationLoader from "@/Components/Skeletons/DataTablePaginationLoader";
import { POUND_SIGN } from "@/Constants/common";
import {
  fetchUnPaidTimesheet,
  saveInvoiceAdjustment,
  updateSelectedTimesheetGroupIds,
} from "@/reducers/timesheets/invoicecreation";
import {
  fetchAuthorisedTimesheets,
  setAuthorisedTimesheetsLoading,
  toggleInvoiceTimesheetModal,
  updateAuthorisedTimesheets,
  updatePagination,
  updateSelectedCandidate,
  updateSorting,
  updateSelectedSignOffIds,
} from "@/reducers/timesheets/timesheetauthorised";
import { fetchTimesheetCount } from "@/reducers/timesheets/timesheets";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";

const CreateInvoiceModal = () => {
  const dispatch = useDispatch();

  const {
    isInvoiceTimesheetModalOpen,
    selectedSignOffIds,
    isLoading,
    isPaginationLoading,
  } = useSelector((state) => state.clientTimesheetAuthorised);

  const { invoiceCreationData, selectedTimesheetGroupIds } = useSelector(
    (state) => state.invoiceCreation,
  );

  const [sendMail, setSendMail] = useState(false);
  const [toggleSelected, setToggleSelected] = useState(false);

  const columns = [
    {
      name: "REF NO",
      sortable: false,
      sortField: "timesheet_refno",
      selector: (row) => row.timesheet_refno,
      width: "120px", // compact but readable
      wrap: false,
    },
    {
      name: "AUTHORISED DATE",
      sortable: false,
      sortField: "timesheet_authorized_date",
      selector: (row) =>
        row.timesheet_authorized_date
          ? moment(row.timesheet_authorized_date).format("DD/MM/YYYY")
          : "-",
      width: "170px",
      wrap: false,
    },
    {
      name: "CANDIDATE NAME",
      sortable: false,
      sortField: "candidate_name",
      selector: (row) =>
        row?.candidate?.candidate_forename +
        " " +
        row?.candidate?.candidate_surname,
      width: "210px",
      wrap: false,
    },
    {
      name: "JOB TITLE",
      sortable: false,
      sortField: "vacancy_job_title",
      selector: (row) => row?.vacancy?.vacancy_job_title,
      width: "215px",
      wrap: true,
    },
    {
      name: "AMOUNT",
      sortable: false,
      sortField: "client_pay",
      selector: (row) =>
        POUND_SIGN +
        " " +
        (row.client_pay ? Number(row.client_pay).toFixed(2) : "-"),
      width: "150px",
      wrap: false,
    },
  ];

  const dataTableRef = useRef();

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchUnPaidTimesheet({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(deSelectAllTimesheets());
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setAuthorisedTimesheetsLoading(true));
    dispatch(fetchAuthorisedTimesheets());
  };

  const handleSave = async () => {
    await dispatch(
      saveInvoiceAdjustment({
        timesheet_group_ids: selectedTimesheetGroupIds.join(","),
      }),
    );

    handleClose();

    // Reset pagination to page 1 and clear existing data
    dispatch(
      updatePagination({
        currentPage: 1,
        totalPage: 1,
        isLastPage: false,
      }),
    );

    dispatch(fetchAuthorisedTimesheets());

    // Clear existing timesheets to force fresh fetch
    dispatch(updateAuthorisedTimesheets([]));

    // Fetch fresh authorised timesheets
    dispatch(setAuthorisedTimesheetsLoading(true));
    dispatch(fetchAuthorisedTimesheets());
    dispatch(fetchTimesheetCount());
  };

  const handleClose = () => {
    dispatch(toggleInvoiceTimesheetModal(false));
    setToggleSelected(!toggleSelected);
    dispatch(updateSelectedTimesheetGroupIds([]));
    dispatch(updateSelectedSignOffIds([]));
    dispatch(updateSelectedCandidate([]));
  };

  return (
    <ModalPopUp
      show={isInvoiceTimesheetModalOpen}
      panelClass="max-w-[980px]"
      onClose={handleClose}
    >
      <div className="p-2">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between border-b border-gray-300 pb-3">
          <h2 className="text-xl font-semibold">Add Timesheets to invoice</h2>
          <button
            onClick={handleClose}
            className="text-red outline-none ring-0"
          >
            <RejectIcon />
          </button>
        </div>

        {/* DataTable */}
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={invoiceCreationData}
            fixedHeader
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeaderScrollHeight="600px"
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            selectableRows
            onSelectedRowsChange={(state) => {
              let selectedTimesheetGroupIds = state.selectedRows.map((item) => {
                return item.id;
              });
              dispatch(
                updateSelectedTimesheetGroupIds(selectedTimesheetGroupIds),
              );
            }}
            noDataComponent={<TableNoContent rows={5} />}
            clearSelectedRows={toggleSelected}
            // expandableRows
            // expandOnRowClicked
            // expandableRowsComponent={Expandedrow}
            className="_DataTable"
          />
        </div>

        {!isLoading && isPaginationLoading && <DataTablePaginationLoader />}

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className="transition-linear relative h-[45px] w-[90px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary"
              disabled={!selectedSignOffIds.length}
            >
              Invoice
            </button>

            <button
              onClick={handleClose}
              className="transition-linear relative h-[45px] w-[80px] rounded-lg border border-primary bg-white text-[14px] font-medium text-blue hover:bg-blue hover:text-white"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalPopUp>
  );
};

export default CreateInvoiceModal;
